import React from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useParams} from 'react-router-dom';
import _ from 'lodash';
import {Box, Container, Grid, Skeleton, Stack, Typography} from '@mui/material';
import {useGetOneYouTubeProfileHistory} from '../../api/graphql_profile_history';
import {useGetHistoryScoresOverTime} from '../../api/graphql_history_score_overtime';
import {VideoCard} from '../YouTube/VideoCard';
import {Sparkline} from '../YouTube/Sparkline';
import {HistoryVideo} from '../../types/generated/graphql';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

interface WatchEvent {
  video: HistoryVideo;
  watchDate: Date | null;
}

interface BucketGroup {
  key: string;
  title: string;
  caption: string;
  items: WatchEvent[];
  sortKey: number;
}

// Monday-anchored UTC start-of-week, matching the canonicalizer used
// server-side by `watch_events`. The server returns ISO strings of
// canonical bucket starts; the grouping below partitions those into
// "Today / Yesterday / Earlier this week / Last week / Earlier this
// month / <Month YYYY>" labels for display only.
function startOfIsoWeekUTC(d: Date): Date {
  const day = (d.getUTCDay() + 6) % 7;
  const out = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  out.setUTCDate(out.getUTCDate() - day);
  return out;
}

function startOfDayUTC(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

// All `watchedAt` values flowing in from `watch_events` are anchored
// at midnight UTC (canonical bucket starts: `utcDayStart`,
// `startOfMonth`, `startOfIsoWeek` in db/utils/watch_date_bucket.ts
// all produce UTC midnight). Rendering them with date-fns `format`
// would use the *local* timezone, which silently shifts April 1
// 00:00 UTC into March 31 evening for any negative-offset TZ — and
// then a section header reads "March 2026" while videos with the
// same UTC-midnight anchor render "Apr 24" elsewhere on the card.
// We render exclusively in UTC to keep the canonicalization-render
// pipeline self-consistent.
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = MONTHS_LONG.map(m => m.slice(0, 3));
const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday',
  'Thursday', 'Friday', 'Saturday',
];

function fmtMonthYearUTC(d: Date): string {
  return `${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function fmtMonthDayUTC(d: Date): string {
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function fmtWeekdayMonthDayUTC(d: Date): string {
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function fmtWeekdayShortMonthDayUTC(d: Date): string {
  const wd = WEEKDAYS[d.getUTCDay()].slice(0, 3);
  return `${wd}, ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function bucketize(events: WatchEvent[]): BucketGroup[] {
  const today = startOfDayUTC(new Date());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const thisWeekStart = startOfIsoWeekUTC(new Date());
  const lastWeekStart = new Date(thisWeekStart.getTime() - 7 * 86_400_000);
  const thisMonthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const lastWeekEnd = new Date(thisWeekStart.getTime() - 86_400_000);

  const groups = new Map<string, BucketGroup>();
  const ensure = (
    key: string,
    title: string,
    caption: string,
    sortKey: number,
  ): BucketGroup => {
    let g = groups.get(key);
    if (!g) {
      g = {key, title, caption, items: [], sortKey};
      groups.set(key, g);
    }
    return g;
  };

  for (const item of events) {
    const wd = item.watchDate;
    if (!wd) {
      ensure('unknown', 'Date unknown', 'No watch date recorded', -1).items.push(item);
      continue;
    }
    const dayStart = startOfDayUTC(wd);

    // Cascading disjoint cascade — each branch eliminates a range
    // the next branch doesn't have to re-check. Crucially, the
    // "Earlier this week" range can be empty (when today is Monday
    // there are no in-week days other than today), and the
    // "Earlier this month" range can be empty (when last-week-start
    // is earlier than this-month-start). Both fall through cleanly.
    //
    // sortKey scheme: named buckets get fixed values 6_000_000+;
    // month buckets use `monthStart.getTime() / 1e9` (a small
    // number, ~1.7e3 for any 2026 month) so they always sort BELOW
    // the named buckets but among themselves are still in
    // recency-descending order. Earlier mistake had month sortKey
    // as `getTime()` itself (~1.7e12 ms), which dwarfed the small
    // named-bucket sortKeys and pushed every month bucket to the
    // top of the page.
    if (dayStart.getTime() === today.getTime()) {
      ensure('today', 'Today', fmtWeekdayMonthDayUTC(today), 6_000_000).items.push(item);
    } else if (dayStart.getTime() === yesterday.getTime()) {
      ensure(
        'yesterday',
        'Yesterday',
        fmtWeekdayMonthDayUTC(yesterday),
        5_000_000,
      ).items.push(item);
    } else if (dayStart >= thisWeekStart) {
      ensure(
        'this-week',
        'Earlier this week',
        `Since ${fmtWeekdayShortMonthDayUTC(thisWeekStart)}`,
        4_000_000,
      ).items.push(item);
    } else if (dayStart >= lastWeekStart) {
      ensure(
        'last-week',
        'Last week',
        `${fmtMonthDayUTC(lastWeekStart)} – ${fmtMonthDayUTC(lastWeekEnd)}`,
        3_000_000,
      ).items.push(item);
    } else if (dayStart >= thisMonthStart) {
      ensure(
        'earlier-month',
        'Earlier this month',
        fmtMonthYearUTC(thisMonthStart),
        2_000_000,
      ).items.push(item);
    } else {
      const monthStart = new Date(
        Date.UTC(dayStart.getUTCFullYear(), dayStart.getUTCMonth(), 1),
      );
      const yearMonth = `${monthStart.getUTCFullYear()}-${String(monthStart.getUTCMonth() + 1).padStart(2, '0')}`;
      const key = `month-${yearMonth}`;
      ensure(
        key,
        fmtMonthYearUTC(monthStart),
        String(monthStart.getUTCFullYear()),
        Math.round(monthStart.getTime() / 1_000_000_000),
      ).items.push(item);
    }
  }

  for (const g of groups.values()) {
    g.items.sort(
      (a, b) =>
        (b.watchDate?.getTime() ?? 0) - (a.watchDate?.getTime() ?? 0),
    );
  }
  return Array.from(groups.values()).sort((a, b) => b.sortKey - a.sortKey);
}

function HistoryHeader({
  totalEvents,
  sparkSeries,
  loading,
}: {
  totalEvents: number;
  sparkSeries: number[];
  loading: boolean;
}): React.JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: {xs: 'flex-start', sm: 'center'},
        flexDirection: {xs: 'column', sm: 'row'},
        gap: 2,
        py: 3,
      }}
    >
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography sx={{fontSize: 22, fontWeight: 800, color: '#0F172A', lineHeight: 1.2}}>
          Watch history
        </Typography>
        <Typography sx={{fontSize: 13, color: '#64748B', mt: 0.5}}>
          {loading
            ? 'Loading watch events…'
            : `${totalEvents.toLocaleString()} watch event${totalEvents === 1 ? '' : 's'} grouped by when they happened`}
        </Typography>
      </Box>
      {sparkSeries.length > 0 && (
        <Box sx={{color: '#3448F0'}}>
          <Sparkline data={sparkSeries} width={140} height={32} stroke="#3448F0" />
        </Box>
      )}
    </Box>
  );
}

function GroupSkeleton(): React.JSX.Element {
  return (
    <Box sx={{mb: 4}}>
      <Skeleton variant="text" width={160} height={20} sx={{mb: 0.5}} />
      <Skeleton variant="text" width={220} height={14} sx={{mb: 1.5}} />
      <Grid container spacing={{xs: 2, md: 3}} columns={{xs: 4, sm: 8, md: 12}}>
        {Array.from({length: 6}).map((_, i) => (
          <Grid key={i} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
            <Skeleton variant="rounded" height={180} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function OneProfileHistory({client}: Props): React.JSX.Element {
  const {pid} = useParams();
  const {data, loading, error} = useGetOneYouTubeProfileHistory(client, pid as string);
  const {data: trend} = useGetHistoryScoresOverTime(client, pid as string);

  const sparkSeries = _.takeRight(
    _.sortBy(trend?.getHistoryScoresOverTime?.dailyScores ?? [], d => d.date).map(
      d => d.score ?? 0,
    ),
    7,
  );

  // Expand each video into one entry per distinct watch event. The
  // resolver returns `watched_on` as an ISO list of canonical bucket
  // starts (one per `watch_events` row the video appears in). A video
  // watched in two different buckets shows up as two cards — once in
  // each bucket — which is the natural way to express rewatch.
  //
  // Falls back to a single entry from `watch_date` when `watched_on`
  // is missing (legacy rows). Dates that don't parse get a null
  // `watchDate` and land in the "Date unknown" group.
  const watchEvents: WatchEvent[] = React.useMemo(() => {
    const out: WatchEvent[] = [];
    for (const video of data?.getOneYouTubeProfileHistory?.videos ?? []) {
      const isos = video.watched_on && video.watched_on.length > 0
        ? video.watched_on
        : video.watch_date
          ? [video.watch_date]
          : [];
      if (isos.length === 0) {
        out.push({video, watchDate: null});
        continue;
      }
      for (const iso of isos) {
        const dt = new Date(iso);
        out.push({
          video,
          watchDate: Number.isNaN(dt.getTime()) ? null : dt,
        });
      }
    }
    return out;
  }, [data]);

  const groups = React.useMemo(() => bucketize(watchEvents), [watchEvents]);

  return (
    <Box sx={{minHeight: '100vh', bgcolor: '#F8FAFC'}}>
      <Container maxWidth="md" sx={{py: 0}}>
        <HistoryHeader
          totalEvents={watchEvents.length}
          sparkSeries={sparkSeries}
          loading={loading && !data}
        />

        {error && (
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid #FECACA',
              bgcolor: '#FEF2F2',
              p: 3,
              mb: 3,
            }}
          >
            <Typography sx={{fontSize: 13, color: '#991B1B'}}>
              Couldn&rsquo;t load history: {error.message}
            </Typography>
          </Box>
        )}

        {loading && !data && (
          <Box sx={{pb: 6}}>
            <GroupSkeleton />
            <GroupSkeleton />
          </Box>
        )}

        {data && pid && watchEvents.length === 0 && (
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              p: 4,
              textAlign: 'center',
              mb: 6,
            }}
          >
            <Typography sx={{fontSize: 14, color: '#64748B'}}>
              No watch history yet. Once the desktop agent syncs, watched videos will show
              up here grouped by day and week.
            </Typography>
          </Box>
        )}

        {data && pid && groups.length > 0 && (
          <Stack spacing={4} sx={{pb: 6}}>
            {groups.map(group => (
              <Box key={group.key}>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  sx={{mb: 1.5}}
                >
                  <Box>
                    <Typography sx={{fontSize: 14, fontWeight: 700, color: '#0F172A'}}>
                      {group.title}
                    </Typography>
                    <Typography sx={{fontSize: 12, color: '#64748B', mt: 0.25}}>
                      {group.caption}
                    </Typography>
                  </Box>
                  <Typography sx={{fontSize: 12, color: '#64748B', fontWeight: 600}}>
                    {group.items.length} video{group.items.length === 1 ? '' : 's'}
                  </Typography>
                </Stack>
                <Grid
                  container
                  spacing={{xs: 2, md: 3}}
                  columns={{xs: 4, sm: 8, md: 12}}
                >
                  {group.items.map(({video, watchDate}, idx) => (
                    <Grid
                      key={`${video.id}-${watchDate?.toISOString() ?? 'undated'}-${idx}`}
                      size={{xs: 6, sm: 4, md: 3, lg: 2}}
                    >
                      <VideoCard
                        pid={pid}
                        client={client}
                        video={video}
                        recommendedAt={watchDate ? fmtMonthDayUTC(watchDate) : undefined}
                        timestampLabel="Watched"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
